import type { RiskClass, RunStatus } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `activity` — the operator plane: what the platform did, what it cost, whether it worked,
 * and where the bytes are.
 *
 * ── The largest extraction in PRD-27, and it never had a catalogue ──
 *
 * Every string here came out of three components that predate AGM-124 —
 * `pages/ActivityPage.tsx`, `components/activity/StoragePanel.tsx` and
 * `AgentUsagePanel.tsx` — plus the five counted nouns that were living as English literals
 * inside `storage-presentation.ts` (PRD-27 F-14, F-12, LOC-010). The two pure modules beside
 * those components keep everything that is NOT copy: `capacity`, `pressureOf`,
 * `volumeReading`, `rankedSources`, `formatShare`, and the whole of `agent-usage.ts`
 * (LOC-017, D-25).
 *
 * ── This screen's one rule, and what it means for a translator ──
 *
 * It never says more than it was told. Every number names an endpoint field; a metric with
 * no series keeps its card and loses its chart; a volume that could not be read says so
 * rather than reading zero. So the hedges in these sentences are load-bearing and survive
 * translation intact: *median* is not *average*, "this says nothing about how full anything
 * is" is the whole point of the storage failure line, and "not enough yet" is a refusal to
 * report a rate rather than a rate of zero.
 *
 * ── Why the panel states take a KEY and not a noun phrase ──
 *
 * `PanelBusy` and `PanelFailure` used to take an English noun phrase *with its article* —
 * `what="the model registry"`, `what="your runs"` — and drop it into the middle of a
 * sentence, where in `PanelFailure` it was the SUBJECT. That reads as good DRY React and has
 * no rendering in a gendered language at all: Portuguese would have to agree the participle
 * with six different nouns it never sees (F-10, D-20). Each state is now a whole sentence
 * chosen by a semantic key, which is also what lets the Portuguese sidestep the agreement
 * entirely by starting from the verb.
 *
 * English is the source of truth AND the type (LOC-011). Anything that interpolates is a
 * FUNCTION returning a whole phrase, never a template assembled at the call site (LOC-013).
 */

/** See the note in `chat.ts`: structural, so this file does not close a circle on `I18n`. */
type Counting = { locale: Locale; formatCount: (value: number) => string }

/**
 * Picks the form CLDR says this count takes.
 *
 * Never `count === 1`: `Intl.PluralRules('pt-BR').select(0)` returns `one`, so Portuguese
 * takes the singular at zero ("0 arquivo") while English takes the plural ("0 files") — a
 * two-branch comparison is already wrong in one shipped language on every empty state, and
 * an empty state is what a fresh deployment is (D-19).
 */
const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

/**
 * Every operation risk, as the word the badge shows.
 *
 * `satisfies Record<RiskClass, string>` is the point (D-9, LOC-018): a class added to the
 * contract is a compile error in both catalogues rather than a raw `external_communication`
 * on screen. `governance` words the same enum again on purpose — see the note there.
 */
const risk = {
  read: 'read',
  reversible_write: 'reversible write',
  external_communication: 'external communication',
  privileged: 'privileged',
  financial: 'financial',
  destructive: 'destructive'
} satisfies Record<RiskClass, string>

/**
 * A run's status, as the word the badge shows.
 *
 * A run is *execução* in Portuguese and therefore feminine, so every one of these is a
 * participle that has to agree with it — which is exactly the distinction English cannot
 * show and a translator cannot infer from the source (ADR-0018's gender column).
 */
const runStatus = {
  queued: 'queued',
  running: 'running',
  waiting_for_approval: 'waiting for approval',
  waiting_for_input: 'waiting for input',
  waiting_for_external: 'waiting for external',
  suspended: 'suspended',
  completed: 'completed',
  partially_completed: 'partially completed',
  failed: 'failed',
  cancelled: 'cancelled',
  expired: 'expired'
} satisfies Record<RunStatus, string>

/**
 * The counted nouns this namespace leans on, hoisted so the entries that compose them do not
 * reference `activity` inside its own initializer — which TypeScript cannot infer a type for.
 *
 * They were English literals inside `storage-presentation.ts` until now, behind a generic
 * `plural(count, noun)` helper that took the noun as a call-site argument and so hid it from
 * extraction entirely (F-12). That helper is gone; these are the words it was hiding.
 */
const files = (fmt: Counting, count: number): string =>
  plural(fmt, count, { one: `${fmt.formatCount(count)} file`, other: `${fmt.formatCount(count)} files` })

const passages = (fmt: Counting, count: number): string =>
  plural(fmt, count, { one: `${fmt.formatCount(count)} passage`, other: `${fmt.formatCount(count)} passages` })

const bases = (fmt: Counting, count: number): string =>
  plural(fmt, count, { one: `${fmt.formatCount(count)} base`, other: `${fmt.formatCount(count)} bases` })

const knowledgeBases = (fmt: Counting, count: number): string =>
  plural(fmt, count, {
    one: `${fmt.formatCount(count)} knowledge base`,
    other: `${fmt.formatCount(count)} knowledge bases`
  })

const runsCount = (fmt: Counting, count: number): string =>
  plural(fmt, count, { one: `${fmt.formatCount(count)} run`, other: `${fmt.formatCount(count)} runs` })

export const activity = {
  // --- the gate -----------------------------------------------------------

  operatorOnly: {
    title: 'Operator access required',
    body: 'This view shows platform-wide usage and spend.'
  },

  // --- the page -----------------------------------------------------------

  title: 'Activity',
  lead: 'Usage, latency and spend across the platform. The runs listed below are your own.',
  /** The window pills. Only 7, 30 and 90 are offered, and all three are plural in both. */
  rangeDays: (fmt: Counting, days: number): string =>
    plural(fmt, days, { one: `${fmt.formatCount(days)} day`, other: `${fmt.formatCount(days)} days` }),

  // --- the four cards -----------------------------------------------------

  cards: {
    runs: 'Runs',
    taskSuccess: 'Task success',
    latency: 'Latency',
    spend: 'Spend'
  },

  /** What a screen reader gets instead of thirty unlabelled rectangles. */
  runsPerDay: (fmt: Counting, days: number): string => `Runs per day over the last ${fmt.formatCount(days)} days.`,
  spendPerDay: (fmt: Counting, days: number): string => `Spend per day over the last ${fmt.formatCount(days)} days.`,

  completedAndFailed: (fmt: Counting, completed: number, failed: number): string =>
    `${fmt.formatCount(completed)} completed · ${fmt.formatCount(failed)} failed`,
  /**
   * The latency card's second line. `p95` is a term of art and stays as it is (D-15); the
   * duration arrives already formatted.
   *
   * The card above it reports the MEDIAN, and that word is not decoration — a median and a
   * mean answer different questions about the same data, and a translation that reaches for
   * "média" would quietly restate the claim.
   */
  p95: (duration: string): string => `p95 ${duration}`,
  perSuccessfulRun: (cost: string): string => `${cost} per successful run`,

  /** Under a percentage point of movement in either direction is not a trend. */
  steady: 'steady',
  againstPrevious: (fmt: Counting, days: number): string => `against the previous ${fmt.formatCount(days)} days`,

  // --- the chart ----------------------------------------------------------

  dailySpend: 'Daily spend',
  peak: (cost: string): string => `peak ${cost}`,
  /**
   * The hover title on one column. `day` is the API's own bucket key (`YYYY-MM-DD`) and
   * stays verbatim: it is an identifier a reader quotes back, not a date this screen chose
   * to render.
   */
  dayTooltip: (fmt: Counting, day: string, cost: string, runs: number): string =>
    `${day} — ${cost} over ${plural(fmt, runs, {
      one: `${fmt.formatCount(runs)} run`,
      other: `${fmt.formatCount(runs)} runs`
    })}`,

  // --- the run log --------------------------------------------------------

  recentRuns: 'Recent runs',
  recentRunsHint: 'open a run in its conversation',
  noRuns: 'No runs yet. Anything you ask the assistant appears here, with its cost.',

  runsCount,
  /** The two halves of a run's work, in the 9.5px line under its question. */
  modelCalls: (fmt: Counting, count: number): string =>
    plural(fmt, count, { one: `${fmt.formatCount(count)} model`, other: `${fmt.formatCount(count)} model` }),
  toolCalls: (fmt: Counting, count: number): string =>
    plural(fmt, count, { one: `${fmt.formatCount(count)} tool`, other: `${fmt.formatCount(count)} tools` }),

  runStatus,
  /**
   * A status this client has never heard of means a client older than the server, and it
   * says so rather than printing the wire token at a reader.
   */
  unknownStatus: 'unknown',

  // --- by model, and the registry ----------------------------------------

  byModel: 'By model',
  noModelCalls: 'No model calls in this window.',
  callsCount: (fmt: Counting, count: number): string =>
    plural(fmt, count, { one: `${fmt.formatCount(count)} call`, other: `${fmt.formatCount(count)} calls` }),
  tokensInOut: (input: string, output: string): string => `${input} in / ${output} out`,

  registeredModels: 'Registered models',
  noModels: 'No models are registered on this deployment.',
  /**
   * `ctx` and `MTok` are the units a deployment's model registry is written in, and they
   * stay in English in every language for the same reason `Top-k` does (D-15). The prices
   * are the registry's own figures and are USD by definition, so only the context window
   * goes through a formatter.
   */
  modelSpec: (contextK: string, inputPerMTok: number, outputPerMTok: number): string =>
    `${contextK}k ctx · $${inputPerMTok}/$${outputPerMTok} per MTok`,

  // --- tools --------------------------------------------------------------

  tools: 'Tools',
  noTools: 'No tools are registered on this deployment.',
  risk,
  approvalBadge: 'approval',
  /** The destinations arrive joined by the one `Intl.ListFormat` in the repository (LOC-016). */
  dataReaches: (destinations: string): string => `data reaches: ${destinations}`,

  // --- people -------------------------------------------------------------

  topUsers: 'Top users',
  noActivity: 'No activity in this window.',

  // --- panel states -------------------------------------------------------

  /**
   * One whole sentence per waiting panel, chosen by key. The spinner beside it is
   * `aria-hidden`, so these words are the entire announcement.
   */
  busy: {
    runs: 'Loading your runs…',
    usage: 'Loading usage…',
    models: 'Loading the model registry…',
    tools: 'Loading the tool registry…'
  },

  /**
   * And one per failed panel. Each says WHICH fetch failed, because one broken query can
   * empty several panels and "something went wrong" sends an operator looking in the wrong
   * place.
   */
  failure: {
    usage:
      'Usage and spend could not be loaded. The platform did not answer — try again, and check that the API is reachable if it keeps failing.',
    runs: 'The run history could not be loaded. The platform did not answer — try again, and check that the API is reachable if it keeps failing.',
    usageByModel:
      'Usage by model could not be loaded. The platform did not answer — try again, and check that the API is reachable if it keeps failing.',
    models:
      'The model registry could not be loaded. The platform did not answer — try again, and check that the API is reachable if it keeps failing.',
    tools:
      'The tool registry could not be loaded. The platform did not answer — try again, and check that the API is reachable if it keeps failing.',
    usageByPerson:
      'Usage by person could not be loaded. The platform did not answer — try again, and check that the API is reachable if it keeps failing.'
  },

  // --- where the bytes are ------------------------------------------------

  storage: {
    title: 'Storage',
    measured: (when: string): string => `measured ${when}`,
    busy: 'Measuring storage…',
    failed:
      'Storage could not be measured. This says nothing about how full anything is — try again, and check that the API can reach its database and its artifact volume if it keeps failing.',

    documents: 'Documents',
    vectorIndex: 'Vector index',
    artifacts: 'Artifacts',
    database: 'Database',

    /** The four counted nouns this screen leans on, each owning its own forms. */
    files,
    passages,
    bases,
    knowledgeBases,

    /** The documents card's census. The unreadable tail appears only when there is one. */
    documentsCensus: (
      fmt: Counting,
      counts: { files: number; bases: number; indexed: number; unreadable: number }
    ): string =>
      `${files(fmt, counts.files)} across ${knowledgeBases(fmt, counts.bases)} · ${fmt.formatCount(counts.indexed)} indexed${
        counts.unreadable > 0 ? ` · ${fmt.formatCount(counts.unreadable)} unreadable` : ''
      }`,

    /** The vector card's. "Not embedded" is a backlog, not a failure, and is worded as one. */
    vectorCensus: (fmt: Counting, counts: { passages: number; dimensions: number; notEmbedded: number }): string =>
      `${passages(fmt, counts.passages)} · ${plural(fmt, counts.dimensions, {
        one: `${fmt.formatCount(counts.dimensions)} dimension`,
        other: `${fmt.formatCount(counts.dimensions)} dimensions`
      })}${counts.notEmbedded > 0 ? ` · ${fmt.formatCount(counts.notEmbedded)} not embedded` : ''}`,

    producedByRuns: (fmt: Counting, count: number): string => `${files(fmt, count)} produced by runs`,

    fullestBase: 'fullest base',
    fullestValue: (share: string, limit: string): string => `${share} of ${limit}`,
    /** A base the reader cannot open is shown by whose it is, never by name (PRD-26 D-54). */
    unnamedBaseOf: (owner: string): string => `a base of ${owner}’s`,
    nearItsLimit: 'near its limit',
    noLimitedBase: 'No knowledge base has a storage limit yet.',

    shareOfDatabase: 'share of database',
    /** Not "0%": a database whose size could not be read is unknown, and says so. */
    unknownShare: 'unknown',
    vectorBreakdown: (index: string, payload: string, total: string): string =>
      `${index} index · ${payload} of embeddings in ${total} total`,

    volume: 'volume',
    volumeUsed: (share: string): string => `${share} used`,
    freeOfTotal: (free: string, total: string): string => `${free} free of ${total}`,
    volumeUnreadable: 'The volume could not be measured. This is not a reading of zero.',

    reclaimable: (bytes: string): string => `${bytes} reclaimable`,
    /**
     * Subject and verb in one entry, because they agree with each other — splitting them
     * would leave the verb to be chosen by a second count somewhere else in the sentence.
     */
    stillStored: (fmt: Counting, count: number): string =>
      plural(fmt, count, {
        one: `${fmt.formatCount(count)} removed document is still stored so citations already given out still resolve.`,
        other: `${fmt.formatCount(count)} removed documents are still stored so citations already given out still resolve.`
      }),
    nothingReclaimable: 'Nothing removed is still taking up room.',

    tableTitle: 'What is taking up the room',
    shownOfBases: (fmt: Counting, shown: number, total: number): string =>
      `${fmt.formatCount(shown)} of ${bases(fmt, total)}`,
    /**
     * The one surprising column, explained once: embeddings are routinely several times the
     * size of the file they came from, and an operator who reads that as a bug goes looking
     * for one.
     */
    embeddingsNote:
      'Embeddings are the vectors derived from a base’s files, and are often larger than the files themselves.',
    nothingIndexed: 'Nothing has been indexed on this deployment yet.',

    columns: {
      base: 'Knowledge base',
      files: 'Files',
      passages: 'Passages',
      embeddings: 'Embeddings',
      size: 'Size',
      share: 'Share'
    },

    baseYouCannotOpen: 'A base you cannot open',
    /**
     * Two entries rather than one taking the kind, because the two differ by a word rather
     * than by a grammar the name could change. Both are prefixes read to a screen reader
     * with the owner's name after them, and the space between is the view's (LOC-014).
     */
    ownedBy: 'Owned by',
    ownedByGroup: 'Owned by the group',
    noOwner: 'no owner',
    lastAdded: (when: string): string => `last added ${when}`,
    ofItsLimit: (share: string): string => `${share} of its limit`
  },

  // --- whose agent cost it ------------------------------------------------

  agents: {
    title: 'By agent',
    summary: (fmt: Counting, counts: { agents: number; runs: number; cost: string; days: number }): string =>
      `${plural(fmt, counts.agents, {
        one: `${fmt.formatCount(counts.agents)} agent`,
        other: `${fmt.formatCount(counts.agents)} agents`
      })} · ${runsCount(fmt, counts.runs)} · ${counts.cost} over ${fmt.formatCount(counts.days)} days`,
    /**
     * Why this does not add up to the Runs card above it. Said here rather than left for the
     * reader to work out, or worse, to distrust.
     */
    previewsExcluded: (fmt: Counting, count: number): string =>
      plural(fmt, count, {
        one: `${fmt.formatCount(count)} builder preview excluded`,
        other: `${fmt.formatCount(count)} builder previews excluded`
      }),

    failed: 'Usage by agent could not be loaded.',
    busy: 'Loading usage by agent…',
    empty:
      'No agent ran in this window. Preview conversations in the builder are excluded on purpose — a maker trying their own draft is not production workload.',

    /**
     * The finding worth acting on, said once above the rows rather than as a badge on each
     * offender. One whole sentence per plural form: the subject is counted and the verb
     * agrees with it, so a fragment would break in both languages rather than one.
     */
    ungrounded: (fmt: Counting, count: number): string =>
      plural(fmt, count, {
        one: 'One agent has knowledge attached and cited none of it in this window — the attached material is being paid for and is not answering anything.',
        other: `${fmt.formatCount(count)} agents have knowledge attached and cited none of it in this window — the attached material is being paid for and is not answering anything.`
      }),

    succeeded: (rate: string): string => `${rate} succeeded`,
    tokens: (count: string): string => `${count} tokens`,
    lastRun: (when: string): string => `last ${when}`,

    /** Two counted facts about grounding, never a score: nothing here measures one. */
    cited: (fmt: Counting, count: number): string =>
      plural(fmt, count, { one: `${fmt.formatCount(count)} cited`, other: `${fmt.formatCount(count)} cited` }),
    emptyRate: (rate: string): string => `${rate} empty`,
    ofSpend: (share: string): string => `${share} of spend`,

    figures: {
      costPerSuccess: 'cost per successful run',
      tokensInOut: 'tokens in / out',
      searches: 'searches',
      foundNothing: 'found nothing',
      failedRuns: 'failed runs'
    },
    /** A refusal to report a rate, not a rate of zero. */
    notEnoughYet: 'not enough yet',

    knowledgeTitle: 'Knowledge it is standing on',
    /**
     * An unattached agent is not an ungrounded one: it searches whatever the person asking
     * may already open, so it can and does cite — there is simply no fixed set to hold those
     * citations against.
     */
    unattachedWithCitations: (fmt: Counting, count: number): string =>
      plural(fmt, count, {
        one: `Nothing is attached, so this agent searches whatever the person asking can already read — its ${fmt.formatCount(count)} citation came from across the catalogue rather than from a fixed set.`,
        other: `Nothing is attached, so this agent searches whatever the person asking can already read — its ${fmt.formatCount(count)} citations came from across the catalogue rather than from a fixed set.`
      }),
    unattached:
      'Nothing is attached to this agent and no answer cited anything. It answers from the model and whatever the person put in front of it.',
    neverCited: 'never cited',

    /**
     * The attachment is the CURRENT one and the citations are the window's, so a base
     * attached yesterday reads as never cited and has done nothing wrong. Said plainly,
     * because this is the line somebody would otherwise act on.
     */
    unusedBases: (fmt: Counting, count: number, days: number): string =>
      plural(fmt, count, {
        one: `One attached base answered nothing in the last ${fmt.formatCount(days)} days. That is worth a look before it is worth a deletion — it may hold the answer to a question nobody asked this month, or it may have been attached only days ago.`,
        other: `${fmt.formatCount(count)} attached bases answered nothing in the last ${fmt.formatCount(days)} days. That is worth a look before it is worth a deletion — it may hold the answer to a question nobody asked this month, or it may have been attached only days ago.`
      })
  }
}
